// src/components/scratch/RemainingPrizeChart.jsx
import React from "react";
import { formatMoney, formatCount } from "../../services/scratchService";

/**
 * Horizontal "remaining vs claimed" bars per prize tier, drawn as inline SVG.
 * Each row shows the share of that tier's prizes still unclaimed (green) vs
 * already claimed (muted), based purely on public counts. Tiers with unknown
 * printed counts show remaining-only.
 *
 * @param {{ tiers: import("../../services/scratchService").ScratchPrizeTierDto[] }} props
 */
export default function RemainingPrizeChart({ tiers = [] }) {
  const rows = [...tiers]
    .sort((a, b) => Number(b.prizeAmount) - Number(a.prizeAmount))
    .slice(0, 12);

  if (rows.length === 0) {
    return (
      <p className="text-[12px]" style={{ color: "var(--txt-3)" }}>
        No prize-tier data available.
      </p>
    );
  }

  const rowH = 30;
  const gap = 10;
  const labelW = 86;
  const barW = 240;
  const width = labelW + barW + 90;
  const height = rows.length * (rowH + gap);

  return (
    <div style={{ overflowX: "auto" }}>
      <svg width="100%" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Remaining prizes by tier">
        {rows.map((t, i) => {
          const y = i * (rowH + gap);
          const printed = Number(t.prizesPrinted) || 0;
          const remaining = Number(t.prizesRemaining) || 0;
          const known = printed > 0;
          const remainingFrac = known ? Math.max(0, Math.min(1, remaining / printed)) : 1;
          const fillW = barW * remainingFrac;

          return (
            <g key={i}>
              <text
                x={0}
                y={y + rowH / 2}
                dominantBaseline="middle"
                style={{ fill: "var(--txt-2)", fontSize: 12, fontWeight: 600 }}
              >
                {formatMoney(t.prizeAmount)}
              </text>

              {/* track */}
              <rect
                x={labelW}
                y={y + 6}
                width={barW}
                height={rowH - 12}
                rx={6}
                style={{ fill: "var(--surf-3)" }}
              />
              {/* remaining */}
              <rect
                x={labelW}
                y={y + 6}
                width={fillW}
                height={rowH - 12}
                rx={6}
                style={{ fill: t.topPrize ? "var(--gold)" : "var(--teal)" }}
              />

              <text
                x={labelW + barW + 8}
                y={y + rowH / 2}
                dominantBaseline="middle"
                style={{ fill: "var(--txt)", fontSize: 11 }}
              >
                {formatCount(remaining)}
                {known ? ` / ${formatCount(printed)}` : " left"}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="flex items-center gap-4 mt-2 text-[11px]" style={{ color: "var(--txt-3)" }}>
        <span className="inline-flex items-center gap-1.5">
          <span style={{ width: 10, height: 10, borderRadius: 3, background: "var(--gold)", display: "inline-block" }} />
          Top prize remaining
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span style={{ width: 10, height: 10, borderRadius: 3, background: "var(--teal)", display: "inline-block" }} />
          Other prizes remaining
        </span>
      </div>
    </div>
  );
}
